import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, watch } from 'vue';
    import { usePage } from '@inertiajs/vue3';
    import { useQuery } from '@tanstack/vue-query';
    import { useI18n } from 'vue-i18n';
    import axios from 'axios';
    import { IdCardLanyard } from '@lucide/vue';
    import { Pencil, Users } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import EditCompany from '@/components/companies/EditCompany.vue';
    import type { Company, SharedData } from '@/types';
    import { triggerDashboardTableRefetch } from '@/composables/dashboardTableRefresh';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardNameActions',
  props: {
    companyId: {},
    refreshDashboard: { type: Boolean }
  },
  setup(__props: any) {

    const props = __props;

    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    const page = usePage<SharedData>();

    const canEditCompany = computed(() => hasPermission(Permission.COMPANIES_UPDATE));
    const canViewUsers = computed(() => hasPermission(Permission.USERS_VIEW));
    const canViewEmployees = computed(() => hasPermission(Permission.EMPLOYEES_VIEW));
    const canAccessCompany = computed(() =>
        page.props.auth.companies.some((company) => Number(company.value) === props.companyId),
    );
    const canOpenCompanyLinks = computed(() => canAccessCompany.value && (canViewUsers.value || canViewEmployees.value));
    const hasActions = computed(() => canEditCompany.value || canOpenCompanyLinks.value);

    const editRequested = ref(false);
    const editOpen = ref(false);

    const { data: company } = useQuery({
        queryKey: ['dashboard-company-edit', props.companyId],
        queryFn: async (): Promise<Company> => {
            const { data } = await axios.get(route('v2.companies.edit-data.get', props.companyId));
            return data;
        },
        enabled: editRequested,
        gcTime: 0,
    });

    const openEdit = () => {
        if (company.value) {
            editOpen.value = true;
            return;
        }

        editRequested.value = true;
    };

    watch(company, (value) => {
        if (value && editRequested.value) {
            editOpen.value = true;
        }
    });

    const openCompanyRouteInNewTab = (routeName: string) => {
        window.open(
            route('v2.dashboard.selected.open', {
                selectedCompanyId: props.companyId,
                redirect_route: routeName,
            }),
            '_blank',
            'noopener',
        );
    };

    // Capture the value now so the callback works even after this component
    // is unmounted by the Inertia page remount that follows a modal save.
    const shouldRefreshDashboard = props.refreshDashboard === true;

    const refreshDashboardTable = () => {
        if (!shouldRefreshDashboard) {
            return;
        }

        triggerDashboardTableRefetch({ forceHydration: true });
    };

return (_ctx: any,_cache: any) => {
  return (hasActions.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "ml-auto flex w-18 shrink-0 items-center justify-end gap-0",
        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
      }, [
        (canEditCompany.value)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 0,
              variant: "ghost_primary",
              size: "icon_sm",
              class: "size-6 text-muted-foreground",
              title: _unref(t)('companies.js.company_edit'),
              onClick: openEdit
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Pencil), { class: "size-3.5" })
              ]),
              _: 1
            }, 8, ["title"]))
          : _createCommentVNode("", true),
        (canAccessCompany.value && canViewUsers.value)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 1,
              variant: "ghost_primary",
              size: "icon_sm",
              class: "size-6 text-muted-foreground",
              title: _unref(t)('users.header'),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (openCompanyRouteInNewTab('v2.users.index')))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Users), { class: "size-3.5" })
              ]),
              _: 1
            }, 8, ["title"]))
          : _createCommentVNode("", true),
        (canAccessCompany.value && canViewEmployees.value)
          ? (_openBlock(), _createBlock(_unref(Button), {
              key: 2,
              variant: "ghost_primary",
              size: "icon_sm",
              class: "size-6 text-muted-foreground",
              title: _unref(t)('employee.employees'),
              onClick: _cache[1] || (_cache[1] = ($event: any) => (openCompanyRouteInNewTab('v2.salary.employees.index')))
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(IdCardLanyard), { class: "size-4" })
              ]),
              _: 1
            }, 8, ["title"]))
          : _createCommentVNode("", true),
        (canEditCompany.value && _unref(company))
          ? (_openBlock(), _createBlock(EditCompany, {
              key: 3,
              company: _unref(company),
              open: editOpen.value,
              "after-success": refreshDashboardTable,
              "hide-trigger": "",
              "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => (editOpen.value = $event))
            }, null, 8, ["company", "open"]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})